import clsx from 'clsx';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { Cart, ExpirationTimer, Icon, Loader, SubMenu, useCartContext } from 'components';
import { useFetch, useSafeState, useUser } from 'hooks';
import { useCurrentRoute } from 'hooks/useCurrentRoute';
import { routes } from 'router';
import { appService } from 'services';
import { setShowCart } from 'store';

import 'assets/styles/components/navigation/submenu.scss';

const Header = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { isLoggedIn, currentUser, resetAllStores } = useUser();
  const { call: subMenuCall, data: subMenuData, loading: loadingSubMenu } = useFetch({ filterUserCompany: true });
  const { call: logoutCall } = useFetch();
  const [isLoading, setIsLoading] = useSafeState(false);
  const { timeRemaining } = useCartContext();
  const { isTravelRoute, isLeisureRoute, isSportRoute, isGiftcardRoute } = useCurrentRoute();

  const { showCart, cartEmpty } = useSelector((state) => state.cart);

  const [submenuData, setSubmenuData] = useSafeState(null);
  const [menuOpen, setMenuOpen] = useSafeState(false);
  const [submenuOpen, setSubmenuOpen] = useSafeState({
    travels: false,
    leisures: false,
    sport: false
  });

  useEffect(() => {
    isLoggedIn && getSubMenu();
  }, [isLoggedIn]);

  const getSubMenu = () => {
    subMenuCall({
      service: appService.getStrapiServiceName(),
      path: '/api/submenus',
      method: 'get'
    });
  };

  const handleHoverDismiss = () => {
    setSubmenuOpen((prev) => ({
      ...prev,
      travels: false,
      leisures: false,
      sport: false
    }));
  };

  const handleHover = (slug) => {
    if (!loadingSubMenu && subMenuData?.data) {
      const data = subMenuData.data.find(({ menuType }) => menuType.name === slug);
      setSubmenuData(data);
      setSubmenuOpen((prev) => ({
        ...prev,
        [slug]: true
      }));
    }
  };

  const logoutUser = async () => {
    if (!currentUser?.token) return;
    try {
      setIsLoading(true);
      const { data } =
        (await logoutCall({
          service: 'castelis',
          path: '/authentication/logout',
          method: 'post',
          query: { token: currentUser.token }
        })) || {};
      if (data?.status === 200) resetAllStores();
    } finally {
      setIsLoading(false);
    }
  };

  const displayCart = () => {
    dispatch(setShowCart(true));
  };

  const closeCart = () => {
    dispatch(setShowCart(false));
  };

  return (
    <header className={clsx('header', !isLoggedIn && 'lg:hidden')} onMouseLeave={handleHoverDismiss}>
      <nav className="flex justify-center gap-2 p-2 z-2 lg:px-20 md:justify-between">
        {isLoggedIn && (
          <button type="button" className="w-4 lg:hidden print:hidden" onClick={() => setMenuOpen(true)}>
            <Icon name="menu" />
          </button>
        )}

        <div className="flex-grow lg:flex-grow-0 order-2">
          <NavLink className="navbar-item flex justify-evenly" to={routes.home.path}>
            <img
              className="w-auto h-14"
              src={appService.loadBrandImage('logo')}
              alt={t('images.logoAlt')}
              width="154"
              height="56"
            />
          </NavLink>
        </div>

        <div className={clsx('menu', menuOpen ? 'opened' : 'closed lg:opened')}>
          <NavLink className="lg:hidden" to={routes.home.path}>
            <img
              className="w-auto h-14"
              src={appService.loadBrandImage('logo')}
              alt={t('images.logoAlt')}
              width="154"
              height="56"
            />
          </NavLink>
          <ul className="flex gap-12 flex-col lg:flex-row lg:gap-4 lg:h-14 xl:gap-8">
            <li
              className="navbar-item flex-col lg:flex-row"
              onMouseEnter={() => handleHover('travels')}
              onMouseLeave={handleHoverDismiss}
            >
              <button
                className={clsx(
                  'item-title',
                  isTravelRoute && !loadingSubMenu && 'text-primary-500',
                  submenuOpen.travels && 'open',
                  loadingSubMenu && 'disabled'
                )}
                type="button"
                onClick={() => {
                  handleHover('travels');
                  setSubmenuOpen((prev) => ({
                    ...prev,
                    travels: !submenuOpen.travels
                  }));
                }}
                disabled={loadingSubMenu}
              >
                <span>{t('navbar.links.travel')}</span>
                {!loadingSubMenu ? <Icon name="chevron-down" /> : <Loader loaderClass="w-5 h-5" />}
              </button>
              {submenuOpen.travels && subMenuData && <SubMenu content={submenuData} setMenuOpen={setMenuOpen} />}
            </li>

            <li className="navbar-item" onClick={() => setMenuOpen(false)}>
              <NavLink
                to={routes.partners.path}
                className={({ isActive }) => (isActive ? 'text-primary-500' : 'text-gray-700')}
              >
                {t('navbar.links.partners')}
              </NavLink>
            </li>

            <li
              className="navbar-item flex-col lg:flex-row"
              onMouseEnter={() => handleHover('leisures')}
              onMouseLeave={handleHoverDismiss}
            >
              <button
                className={clsx(
                  'item-title',
                  isLeisureRoute && !loadingSubMenu && 'text-primary-500',
                  submenuOpen.leisures && 'open',
                  loadingSubMenu && 'disabled'
                )}
                type="button"
                onClick={() => {
                  handleHover('leisures');
                  setSubmenuOpen((prev) => ({
                    ...prev,
                    leisures: !submenuOpen.leisures
                  }));
                }}
              >
                <span>{t('navbar.links.leisure')}</span>
                {!loadingSubMenu ? <Icon name="chevron-down" /> : <Loader loaderClass="w-5 h-5" />}
              </button>

              {submenuOpen.leisures && subMenuData && <SubMenu content={submenuData} setMenuOpen={setMenuOpen} />}
            </li>

            <li
              className="navbar-item flex-col lg:flex-row"
              onMouseEnter={() => handleHover('sport')}
              onMouseLeave={handleHoverDismiss}
            >
              <button
                className={clsx(
                  'item-title',
                  isSportRoute && !loadingSubMenu && 'text-primary-500',
                  submenuOpen.sport && 'open',
                  loadingSubMenu && 'disabled'
                )}
                type="button"
                onClick={() => {
                  handleHover('sport');
                  setSubmenuOpen((prev) => ({
                    ...prev,
                    sport: !submenuOpen.sport
                  }));
                }}
              >
                <span>{t('navbar.links.sport')}</span>
                {!loadingSubMenu ? <Icon name="chevron-down" /> : <Loader loaderClass="w-5 h-5" />}
              </button>

              {submenuOpen.sport && subMenuData && <SubMenu content={submenuData} setMenuOpen={setMenuOpen} />}
            </li>

            <li className="navbar-item" onClick={() => setMenuOpen(false)}>
              <NavLink
                to={routes.giftcardHomePage.path}
                className={() => (isGiftcardRoute ? 'text-primary-500' : 'text-gray-700')}
                end
              >
                {t('navbar.links.giftcards')}
              </NavLink>
            </li>
          </ul>
        </div>
        <div className={clsx('layer', !menuOpen && 'hidden')} onClick={() => setMenuOpen(false)}></div>

        {isLoggedIn && (
          <div className="flex gap-4 justify-between items-center order-3 xl:gap-6 print:hidden">
            <NavLink to={routes.dashboard.children.account.path}>
              <Icon
                name="user"
                classes="text-primary-500 hover:text-yellow-500 w-6 h-6 md:w-7 md:h-7"
                iconStyle="fad"
              />
            </NavLink>

            <div className="relative">
              <button type="button" className="relative" onClick={displayCart} data-test-id="shoppingCartButton">
                <Icon
                  name="shopping-cart"
                  iconStyle="fad"
                  classes="w-6 h-6 md:w-7 md:h-7 text-primary-500 hover:text-yellow-500"
                />
                {!cartEmpty && timeRemaining > 0 && (
                  <div className="bg-yellow-500 rounded-full h-3 w-3 absolute -top-1 -right-1"></div>
                )}
              </button>

              <ExpirationTimer clockOnly />
            </div>

            {showCart && (
              <div className="hidden lg:block lg:fixed lg:inset-0 lg:z-10 lg:bg-black/50" onClick={closeCart}></div>
            )}
            <div className={clsx('cart-container', showCart && 'show-cart')} data-test-id="cart">
              <Cart onClose={closeCart} />
            </div>

            <button
              type="button"
              onClick={logoutUser}
              disabled={isLoading}
              className={clsx('text-primary-500', isLoading ? 'disabled' : 'hover:text-yellow-500')}
              data-test-id="logoutButton"
            >
              <Icon name="right-to-bracket" iconStyle="fad" classes="w-6 h-6 md:w-7 md:h-7" />
              <span className="sr-only">{t('auth:logout.button')}</span>
            </button>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
